import type { ParentType } from '@risksmart-app/domain/src/types/consts/index';
import type { GetFormCustomisationResponseRow } from '@risksmart-app/drizzle/src/types';
import type { GetFormFieldOptionsByParentTypeQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { GetFormFieldOptionsByParentTypeDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import { createLazyQueryHook } from 'src/utils';

type UseLazyGetFormFieldOptionsByParentTypeArgs = {
  parentTypes: ParentType[];
};

/**
 * No eager twin — the tRPC `getFormCustomisation` procedure returns nested
 * `form_configuration` rows each carrying a `fields_config` array. The
 * GraphQL document this replaces reads a flat `form_field_configuration`
 * list, so the fields_config arrays are flattened here (same flatten used by
 * `mapFormCustomisationTrpcToGraphQL`, inlined rather than reused because
 * that helper's return shape also includes form_configuration/
 * form_field_ordering/form_section_configuration, which this query doesn't
 * select).
 */
export const useLazyGetFormFieldOptionsByParentType = createLazyQueryHook<
  UseLazyGetFormFieldOptionsByParentTypeArgs,
  GetFormCustomisationResponseRow[],
  GetFormFieldOptionsByParentTypeQuery
>({
  trpcQuery: (client, { parentTypes }) =>
    client.frontend.formConfiguration.getFormCustomisation.query({
      parentTypes,
    }),
  // FormConfigurationParentType is widened to `string` on the tRPC side
  // (Drizzle's InferQueryModel) but narrowed to `Parent_Type_Enum` on the
  // GraphQL side (codegen) — same nominal mismatch as
  // `useGetFormCustomisationByParentTypes`, routed via `unknown`.
  mapTrpcDataToGraphQL: (data) =>
    ({
      form_field_configuration: data.flatMap((c) => c.fields_config ?? []),
    }) as unknown as GetFormFieldOptionsByParentTypeQuery,
  graphqlDocument: GetFormFieldOptionsByParentTypeDocument,
  graphqlVariables: ({ parentTypes }) => ({ parentTypes }),
});
